<template>
  <div class="flex flex-col gap-1">
    <span v-if="label" class="capitalize-first text-sm font-medium">
      {{ label }}
    </span>
    <UserSelectorWrapper
      :selected-users="visibleAssignedPeople"
      :selected-material-resources="materialResources"
      :filter="filterUser"
      :edition-mode="editionMode"
      :avatar-size="avatarSize"
      :event="event"
      :all-users-availability-items="allUsersAvailabilityItems"
      :all-material-resources-availability-items="
        allMaterialResourcesAvailabilityItems
      "
      @modal-open="$emit('modal-open')"
      @modal-close="$emit('modal-close')"
      @add-material-resource="handleAddMaterialResource"
      @remove-material-resource="handleRemoveMaterialResource"
    >
      <template v-if="fullScreen" #user-list="{ selectedUsers }">
        <div class="flex flex-row flex-wrap gap-2">
          <AssignedUserCard
            v-for="user in selectedUsers"
            :key="user.id"
            :user="user"
          />
        </div>
      </template>
      <template #top-left-cta>
        <SelectComponent
          v-model="selectedRole"
          name="roles"
          :values="selectableRoles"
          multiple
        />
      </template>
      <template #selected-user-panel="{ user }">
        <SelectedAssignedUser
          :user="user"
          :edition-mode="editionMode"
          :fixed-function="
            user.disponibility?.function === resolvedUserFunctionId
              ? resolvedUserFunctionId
              : null
          "
          :all-users-availability-items="allUsersAvailabilityItems"
          @update-disponibility="
            (data) => handleUpdateDisponibility(user.id, data)
          "
          @remove="handleRemoveUser(user.id)"
        />
      </template>
      <template #selectable-user-panel="{ user }">
        <SelectableEventUser
          :user="user"
          :event="event"
          :edition-mode="editionMode"
          :occupations="
            occupations.find((occupation) => occupation.id === user.id)
              ?.assigned_event ?? []
          "
          :all-users-availability-items="allUsersAvailabilityItems"
          @add-user="handleAddUser"
        />
      </template>
    </UserSelectorWrapper>
    <span
      v-if="userFunction && !resolvedUserFunctionId"
      class="text-xs font-medium text-orange-500"
    >
      {{ $t('global.no_default_key_for_function') }}
    </span>
    <span
      v-else-if="resolvedUserFunctionId && isShowingFallbackUsers"
      class="text-xs font-medium text-yellow-500"
    >
      {{ $t('global.no_user_with_function') }}
    </span>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import UserSelectorWrapper from '@c/BaseSelectorComponent/UserSelectorModal/UserSelectorWrapper.vue'
import SelectableEventUser from '@c/Event/SelectableEventUser.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import SelectedAssignedUser from '@c/Event/SelectedAssignedUser.vue'
import AssignedUserCard from '@c/Event/AssignedUserCard.vue'

export default defineComponent({
  name: 'EventUserAssignment',
  components: {
    UserSelectorWrapper,
    SelectableEventUser,
    SelectComponent,
    SelectedAssignedUser,
    AssignedUserCard
  },
  props: {
    assignedPeople: {
      type: Array,
      required: false,
      default: () => []
    },
    assignedPeopleIds: {
      type: Array,
      required: false,
      default: () => []
    },
    materialResources: {
      type: Array,
      required: false,
      default: null
    },
    event: {
      type: Object,
      required: false,
      default: () => ({})
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    occupations: {
      type: Array,
      required: false,
      default: () => []
    },
    allUsersAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    },
    allMaterialResourcesAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    userFunction: {
      type: String,
      required: false,
      default: null
    },
    avatarSize: {
      type: String,
      required: false,
      default: 'small'
    },
    fullScreen: {
      type: Boolean,
      required: false,
      default: false
    },
    singleUser: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:assignedPeople',
    'update:assignedPeopleIds',
    'update:materialResources',
    'modal-open',
    'modal-close'
  ],
  data() {
    return {
      selectedRole: []
    }
  },
  computed: {
    ...mapGetters({
      roles: 'metadata/getRolesMetadata',
      fhSettings: 'metadata/getFuneralHomeSettings',
      usersMetadata: 'metadata/getUsersMetadata',
      materialResourcesMetadata: 'metadata/getMaterialResourcesMetadata',
      assignedPersonFunctions: 'metadata/getAssignedPersonFunctionsMetadata'
    }),
    resolvedUserFunctionId() {
      if (!this.userFunction) return null
      return (
        this.assignedPersonFunctions.find(
          (fn) => fn.default_key === this.userFunction
        )?.id ?? null
      )
    },
    filterRoles() {
      if (!this.userFunction || !this.event?.funeral_home_id) return []
      return (
        this.fhSettings?.[this.event.funeral_home_id]?.['roles_for_position']?.[
          this.userFunction
        ] ?? []
      )
    },
    visibleAssignedPeople() {
      if (!this.userFunction) return this.assignedPeople
      if (!this.resolvedUserFunctionId) {
        return this.assignedPeople.filter((u) => !u.disponibility?.function)
      }
      const withThisFunction = this.assignedPeople.filter(
        (u) => u.disponibility?.function === this.resolvedUserFunctionId
      )
      if (withThisFunction.length === 0) {
        return this.assignedPeople.filter((u) => !u.disponibility?.function)
      }
      return withThisFunction
    },
    isShowingFallbackUsers() {
      if (!this.resolvedUserFunctionId) return false
      const hasFunctionUser = this.assignedPeople.some(
        (u) => u.disponibility?.function === this.resolvedUserFunctionId
      )
      const hasFallbackUsers = this.assignedPeople.some(
        (u) => !u.disponibility?.function
      )
      return !hasFunctionUser && hasFallbackUsers
    },
    selectableRoles() {
      if (this.filterRoles.length === 0) return this.roles
      return this.roles.filter((role) => this.filterRoles.includes(role.id))
    }
  },
  watch: {
    filterRoles: {
      immediate: true,
      handler(val) {
        if (this.userFunction) {
          this.selectedRole = [...val]
        }
      }
    }
  },
  methods: {
    filterUser(user) {
      const byFilterRoles =
        this.filterRoles.length === 0 ||
        user.roles?.some((role) => this.filterRoles.includes(role))
      const bySelectedRole =
        this.selectedRole.length === 0 ||
        user.roles?.filter((role) => this.selectedRole.includes(role)).length >
          0
      return byFilterRoles && bySelectedRole
    },
    emitCollection(collection) {
      this.$emit('update:assignedPeople', collection)
      this.$emit(
        'update:assignedPeopleIds',
        collection.map((u) => ({
          id: u.id,
          assignement_status: u.disponibility?.assignement_status ?? null,
          function: u.disponibility?.function ?? null
        }))
      )
    },
    handleAddUser({ user_id, assignement_status }) {
      const user = this.usersMetadata.find((u) => u.id === user_id)
      if (!user) return

      let baseCollection = this.assignedPeople
      if (this.singleUser && this.resolvedUserFunctionId) {
        baseCollection = baseCollection.filter(
          (u) => u.disponibility?.function !== this.resolvedUserFunctionId
        )
      }

      const existingIndex = baseCollection.findIndex((u) => u.id === user_id)
      if (existingIndex !== -1) {
        this.emitCollection(
          baseCollection.map((u, i) => {
            if (i !== existingIndex) return u
            return {
              ...u,
              disponibility: {
                ...u.disponibility,
                assignement_status,
                function: this.resolvedUserFunctionId
              }
            }
          })
        )
      } else {
        this.emitCollection([
          ...baseCollection,
          {
            id: user.id,
            full_name: user.full_name,
            initials: user.initials,
            avatar_url: user.avatar_url ?? null,
            roles: user.roles ?? [],
            disponibility: {
              id: user_id,
              assignement_status,
              function: this.resolvedUserFunctionId
            }
          }
        ])
      }
    },
    handleUpdateDisponibility(userId, data) {
      this.emitCollection(
        this.assignedPeople.map((u) => {
          if (u.id !== userId) return u
          return { ...u, disponibility: { ...u.disponibility, ...data } }
        })
      )
    },
    handleRemoveUser(userId) {
      this.emitCollection(this.assignedPeople.filter((u) => u.id !== userId))
    },
    handleAddMaterialResource(materialResourceId) {
      const resource = this.materialResourcesMetadata.find(
        (r) => r.id === materialResourceId
      )
      if (!resource) return
      this.$emit('update:materialResources', [
        ...(this.materialResources ?? []),
        { ...resource, disponibility: { id: resource.id } }
      ])
    },
    handleRemoveMaterialResource(disponibilityId) {
      this.$emit(
        'update:materialResources',
        (this.materialResources ?? []).filter(
          (r) =>
            r.disponibility?.id !== disponibilityId && r.id !== disponibilityId
        )
      )
    }
  }
})
</script>
